import React, { useState, useEffect } from "react";
import {
    Box, Paper, Typography, Button, TextField, Dialog,
    DialogTitle, DialogContent, DialogActions, Table, TableBody,
    TableCell, TableContainer, TableHead, TableRow, IconButton,
    Chip, CircularProgress, Alert, Tooltip, InputAdornment,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import SearchIcon from "@mui/icons-material/Search";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import axiosInstance from "./Axiosinstance";

const EMPTY_FORM = { name: "", code: "", description: "" };

const AdminProducts = () => {
    const [products,    setProducts]    = useState([]);
    const [filtered,    setFiltered]    = useState([]);
    const [loading,     setLoading]     = useState(true);
    const [search,      setSearch]      = useState("");
    const [dialogOpen,  setDialogOpen]  = useState(false);
    const [deleteDialog, setDeleteDialog] = useState({ open: false, id: null });
    const [form,        setForm]        = useState(EMPTY_FORM);
    const [editId,      setEditId]      = useState(null);
    const [saving,      setSaving]      = useState(false);
    const [error,       setError]       = useState("");
    const [success,     setSuccess]     = useState("");

    const fetchProducts = () => {
        setLoading(true);
        axiosInstance.get("/api/admin/products")
            .then((res) => {
                setProducts(res.data.data || []);
                setFiltered(res.data.data || []);
            })
            .catch(() => setError("Failed to load products."))
            .finally(() => setLoading(false));
    };

    useEffect(() => { fetchProducts(); }, []);

    useEffect(() => {
        const q = search.toLowerCase();
        setFiltered(
            products.filter(
                (p) =>
                    p.name.toLowerCase().includes(q) ||
                    p.code.toLowerCase().includes(q) ||
                    (p.description || "").toLowerCase().includes(q)
            )
        );
    }, [search, products]);

    const openCreate = () => {
        setForm(EMPTY_FORM);
        setEditId(null);
        setError("");
        setDialogOpen(true);
    };

    const openEdit = (product) => {
        setForm({ name: product.name, code: product.code, description: product.description || "" });
        setEditId(product.id);
        setError("");
        setDialogOpen(true);
    };

    const handleSave = async () => {
        if (!form.name.trim() || !form.code.trim()) {
            setError("Name and Code are required.");
            return;
        }
        setSaving(true);
        setError("");
        try {
            if (editId) {
                await axiosInstance.put(`/api/admin/products/${editId}`, form);
                setSuccess("Product updated successfully.");
            } else {
                await axiosInstance.post("/api/admin/products", form);
                setSuccess("Product created successfully.");
            }
            setDialogOpen(false);
            fetchProducts();
        } catch (err) {
            setError(err.response?.data?.message || "Failed to save product.");
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async () => {
        try {
            await axiosInstance.delete(`/api/admin/products/${deleteDialog.id}`);
            setSuccess("Product deactivated.");
            setDeleteDialog({ open: false, id: null });
            fetchProducts();
        } catch {
            setError("Failed to delete product.");
        }
    };

    return (
        <Box>
            {/* Header */}
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
                <Box>
                    <Typography variant="h6" sx={{ fontWeight: 700 }}>Products</Typography>
                    <Typography variant="body2" color="text.secondary">
                        {filtered.length} product{filtered.length !== 1 ? "s" : ""} found
                    </Typography>
                </Box>
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={openCreate}
                    sx={{
                        textTransform: "none", fontWeight: 600, borderRadius: 2,
                        background: "linear-gradient(135deg, #1976d2, #1565c0)",
                        boxShadow: "0 4px 12px rgba(25,118,210,0.3)",
                    }}
                >
                    Add Product
                </Button>
            </Box>

            {success && <Alert severity="success" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setSuccess("")}>{success}</Alert>}
            {error && !dialogOpen && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setError("")}>{error}</Alert>}

            {/* Search */}
            <TextField
                fullWidth
                placeholder="Search by name, code, or description..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                sx={{ mb: 2.5, "& .MuiOutlinedInput-root": { borderRadius: 2, background: "#fff" } }}
                InputProps={{
                    startAdornment: (
                        <InputAdornment position="start">
                            <SearchIcon sx={{ color: "text.secondary" }} />
                        </InputAdornment>
                    ),
                }}
            />

            {/* Table */}
            <TableContainer component={Paper} elevation={0} sx={{ borderRadius: 3, border: "1px solid rgba(0,0,0,0.08)" }}>
                <Table>
                    <TableHead>
                        <TableRow sx={{ background: "rgba(25,118,210,0.04)" }}>
                            {["#", "Name", "Code", "Description", "Status", "Actions"].map((h) => (
                                <TableCell key={h} sx={{ fontWeight: 700, fontSize: "13px", color: "text.secondary", py: 1.75 }}>
                                    {h}
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading ? (
                            <TableRow>
                                <TableCell colSpan={6} align="center" sx={{ py: 5 }}>
                                    <CircularProgress size={32} />
                                </TableCell>
                            </TableRow>
                        ) : filtered.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={6} align="center" sx={{ py: 5, color: "text.secondary" }}>
                                    No products found.
                                </TableCell>
                            </TableRow>
                        ) : (
                            filtered.map((product, idx) => (
                                <TableRow key={product.id} hover sx={{ "&:last-child td": { border: 0 } }}>
                                    <TableCell sx={{ color: "text.secondary", fontSize: "13px" }}>{idx + 1}</TableCell>
                                    <TableCell sx={{ fontWeight: 600 }}>{product.name}</TableCell>
                                    <TableCell>
                                        <Chip label={product.code} size="small" sx={{ fontWeight: 600, fontSize: "11px", background: "rgba(25,118,210,0.08)", color: "primary.main" }} />
                                    </TableCell>
                                    <TableCell sx={{ color: "text.secondary", fontSize: "13px", maxWidth: 280 }}>
                                        {product.description
                                            ? product.description.length > 80
                                                ? product.description.slice(0, 80) + "..."
                                                : product.description
                                            : "—"}
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            icon={<CheckCircleIcon sx={{ fontSize: "14px !important" }} />}
                                            label={product.isActive ? "Active" : "Inactive"}
                                            size="small"
                                            sx={{
                                                fontWeight: 600, fontSize: "11px",
                                                background: product.isActive ? "rgba(46,125,50,0.1)" : "rgba(211,47,47,0.1)",
                                                color: product.isActive ? "#2e7d32" : "#d32f2f",
                                            }}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <Tooltip title="Edit">
                                            <IconButton size="small" onClick={() => openEdit(product)} sx={{ color: "primary.main", mr: 0.5 }}>
                                                <EditIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Deactivate">
                                            <IconButton size="small" onClick={() => setDeleteDialog({ open: true, id: product.id })} sx={{ color: "error.main" }}>
                                                <DeleteIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Create / Edit Dialog */}
            <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth
                PaperProps={{ sx: { borderRadius: 3 } }}>
                <DialogTitle sx={{ fontWeight: 700, pb: 1 }}>
                    {editId ? "Edit Product" : "Add New Product"}
                </DialogTitle>
                <DialogContent sx={{ pt: "8px !important" }}>
                    {error && <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>{error}</Alert>}
                    <TextField
                        label="Product Name"
                        fullWidth required
                        value={form.name}
                        onChange={(e) => setForm({ ...form, name: e.target.value })}
                        sx={{ mb: 2, mt: 1, "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                    />
                    <TextField
                        label="Product Code"
                        fullWidth required
                        value={form.code}
                        onChange={(e) => setForm({ ...form, code: e.target.value })}
                        disabled={!!editId}
                        helperText={editId ? "Code cannot be changed after creation." : "Unique identifier (e.g. ERP-001)"}
                        sx={{ mb: 2, "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                    />
                    <TextField
                        label="Description"
                        fullWidth multiline rows={3}
                        value={form.description}
                        onChange={(e) => setForm({ ...form, description: e.target.value })}
                        sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                    />
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
                    <Button onClick={() => setDialogOpen(false)} sx={{ textTransform: "none", borderRadius: 2 }}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained" onClick={handleSave} disabled={saving}
                        sx={{ textTransform: "none", fontWeight: 600, borderRadius: 2, px: 3,
                            background: "linear-gradient(135deg, #1976d2, #1565c0)" }}
                    >
                        {saving ? <CircularProgress size={20} sx={{ color: "#fff" }} /> : editId ? "Update" : "Create"}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Confirm Dialog */}
            <Dialog open={deleteDialog.open} onClose={() => setDeleteDialog({ open: false, id: null })}
                PaperProps={{ sx: { borderRadius: 3 } }}>
                <DialogTitle sx={{ fontWeight: 700 }}>Deactivate Product?</DialogTitle>
                <DialogContent>
                    <Typography variant="body2" color="text.secondary">
                        This will mark the product as inactive. It won't be visible to users but data will be preserved.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
                    <Button onClick={() => setDeleteDialog({ open: false, id: null })} sx={{ textTransform: "none", borderRadius: 2 }}>
                        Cancel
                    </Button>
                    <Button variant="contained" color="error" onClick={handleDelete}
                        sx={{ textTransform: "none", fontWeight: 600, borderRadius: 2 }}>
                        Deactivate
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default AdminProducts;
